<div class="dub">
    <div>A course by <a href="https://www.richarddub.net">Dr. Richard Dub</a></div>
    <div>Clarkson University, PHIL-330</div>
</div>

Welcome to the course webpage for Logic for Critical Thinking! Over the course of the semester, you will learn boatloads about critical thinking and reasoning. The skills you hone this semester should carry through into a lifetime of curiosity, orderly thought, and intellectual rigor. 

If all goes well, you will become invincible at argumentation.

## Course Structure

The course consists of 12 modules: an introductory module, one corresponding to each of the ten chapters of the textbook, and an interlude module where we will look at some real-world applications.  Each module will last about a week, though some will be a little longer.

## Schedule

<div id="calendar"></div>

## Readings

You will need to buy a subscription to the Top Hat learning platform and the digital textbook [*Reason Better*](https://tophat.com/marketplace/arts-&-humanities/philosophy/full-course/reason-better-an-interdisciplinary-guide-to-critical-thinking-david-manley/3425/117617/) by David Manley.  It has a bunch of neat web-based features that are useful for online learning, and even if it didn't, it would still be a particularly excellent textbook. If you already have a subscription to Top Hat (because you're using it in another class), you don't need to buy a second subscription: just the textbook.

The textbook isn't optional.  You won't be able to submit assignments without it.

We will also read a number of chapters from Jonathan Weisberg's digital text [*Odds & Ends*](https://jonathanweisberg.org/vip/). It is free. Other readings will be distributed online.

Readings will be made available 1-2 weeks before they are to be discussed in class.  You are expected to have done the readings for a module before we begin discussing that module in class.

## Top Hat

Our Top Hat course code is 144199. If you can't sign up or log in, let me know.  You can e-mail me at rdub@clarkson.edu.

Please explore the platform for a little while!  It's fairly intuitive. We'll go over its features in the first class.

If you need help, you can contact the Top Hat Support Team at support@tophat.com, by clicking the in-app support button, or by calling 1-888-663-5491.

## Class Times and Location

Our course meets MWF from 2:00-2:50 in 112 Snell Hall. 

Our meetings will be largely lecture/presentation based, though there will be plenty of class discussion as well.  Please come prepared to participate.

Recordings of the lectures will be made available. Details on accessing these recordings will appear here once everything is set up.

In the event that we are forced to hold some classes online, over Zoom, I will email you and let you know how we will proceed.

## Grades

Your grade will be based on three different sorts of assessment: in-text comprehension questions, occasional in-class participation questions, and four problem sets of increasing length.

It is possible that I will curve grades up (but you shouldn't bank on this!). I will never curve your grade down.

### In-Text Questions (20%)

Top Hat textbooks have a neat feature: at certain points in the text, you'll be asked comprehension questions. The questions are usually pretty easy, but they do demand that you understand what you have read. They act as checks on your attention and comprehension more than they act as challenges of your skills.

Not all the chapters and readings are available right away. Once I make a module's readings available (a week or two in advance), you can start answering the integrated questions in that reading. As soon as the first class on that module starts, you're expected to have done the reading and attempted all the chapter's questions, but you can continue to change your answers until 11:59 PM of the day of our final class of that module. You can see the due dates on Top Hat or on the calendar above.

> **Note:** there are no in-text questions for the readings for the first Introduction module, so don't worry about the first couple of classes.

TLDR: Your in-text questions for each module should be completed before the first class of that module, but they can be submitted without penalty until the day of the final class of that module. In total, they are worth 20% of your grade.

### In-Class Questions (10%)

Here's another neat feature of Top Hat: I have the ability to pose questions during lectures which you can answer live on your Top Hat account (on your laptop or phone). On occasional classes, I'll ask one, two, or three questions for you to answer. These might come up at any time. You'll have one minute to answer before the question closes.  You get half credit for attempting the question and full credit for answering correctly.

These questions serve as attendance checks and are there mainly to check that you haven't checked out. They aren't primarily meant to be tests of your skills (though I'm sure I'll make some of them tricky).

### Problem Sets (70%)

There will be four problem sets for you to complete through Top Hat.  

They will increase in length and in how much they are worth: the first is worth 10% of your final grade, the second is worth 15%, the third is worth 20%, and the last is worth 25%.

Each will be made available at around the beginning of the month and you'll have about five days to complete them.  The final one will be due during the finals period.

The dates of availability are:

<div class="center">
<table class="pure-table duedates"></table>
</div>

## Office Hours

My Office is at Price 2201.  Please visit [my Calendly page](https://calendly.com/richarddub) to book a session.  In encourage you to come, but please don't show up unannounced without having booked a session. I do a lot of advising so you might stop by and find that I'm all booked up!


## Academic Integrity

You are expected to do graded activities on your own. You can talk about material in the course and strategies for solving general sorts of problem with friends, peers, and tutors (and I encourage you to!), but you should not discuss specific problems that have been assigned. Violations of the Clarkson University academic integrity policy will have serious and disastrous repercussions. The punishment for cheating will always be worse for you than the worst outcome had you not cheated, and it is easier to spot cheating than you think. You can familiarize yourself with the Clarkson undergraduate regulations in Appendix C of the handbook [here](https://www.clarkson.edu/sites/default/files/2020-08/Clarkson-School-Handbook-20-21.pdf).

Don't let this dissuade you from having conversations about your papers and the course material with other classmates outside of class. Conversation is always encouraged!

## Disabilities

Please inform me of special needs that you may have. The sooner you notify me, the better I will be able to accommodate you.

## Metapolicy

I reserve the right to change details in the syllabus at my discretion.  I will only do so fairly and with plenty of warning.

## Official Syllabus

[Download](/assets/Syllabus_PHIL-330.docx)